/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { genericActions } from '@opengeoweb/store';
import { HideSliderButton } from '@opengeoweb/time-slider';
import { handleDateUtilsISOString } from '@opengeoweb/webmap';
import { SoundingsTimeSlider } from './SoundingsTimeSlider';
import { useTimesteps } from '../../api/edr-api/hooks';
import { getNearestTimestep } from '../../api/edr-api/utils';
import { soundingsActions } from '../../store/reducer';
import {
  getIsTimeSliderVisible,
  getSelectedSources,
  getSelectedTime,
} from '../../store/selectors';
import type { SoundingsRootState } from '../../store/types';
import { SoundingsActionOrigin } from '../../store/types';

interface SoundingsTimeSliderConnectProps {
  viewId: string;
  timeInUse?: string;
  syncEnabled?: boolean;
}

export const SoundingsTimeSliderConnect = ({
  viewId,
  timeInUse,
  syncEnabled = false,
}: SoundingsTimeSliderConnectProps): React.ReactElement => {
  const selectedTimeInStore = useSelector((state: SoundingsRootState) =>
    getSelectedTime(state, viewId),
  );
  const selectedTime = timeInUse || selectedTimeInStore;
  const sourceSelections = useSelector((state: SoundingsRootState) =>
    getSelectedSources(state, viewId),
  );
  const isTimeSliderVisible = useSelector((state: SoundingsRootState) =>
    getIsTimeSliderVisible(state, viewId),
  );

  const dispatch = useDispatch();
  const toggleTimeSliderVisibility = React.useCallback(
    (nextIsVisible: boolean) => {
      dispatch(
        soundingsActions.toggleTimeSliderVisibility({
          isTimeSliderVisible: nextIsVisible,
          viewId,
          origin: SoundingsActionOrigin.soundingsComponent,
        }),
      );
    },
    [dispatch, viewId],
  );
  const updateSelectedTime = React.useCallback(
    (selectedTime: string) => {
      if (syncEnabled) {
        dispatch(
          genericActions.setTime({
            sourceId: viewId,
            origin: 'SoundingsTimeSliderConnect',
            value: { currentTime: handleDateUtilsISOString(selectedTime) },
          }),
        );
        return;
      }
      dispatch(
        soundingsActions.updateSelectedTime({
          selectedTime,
          viewId,
          origin: SoundingsActionOrigin.soundingsComponent,
        }),
      );
    },
    [dispatch, syncEnabled, viewId],
  );

  const { data: timesteps } = useTimesteps(sourceSelections);
  const mergedTimesteps = Array.from(new Set(timesteps.flat())).sort();
  const validSelectedTime =
    mergedTimesteps.length > 0 && !mergedTimesteps.includes(selectedTimeInStore)
      ? getNearestTimestep(selectedTimeInStore, mergedTimesteps)
      : selectedTimeInStore;

  React.useEffect(() => {
    // Local mode: keep selectedTime aligned with available timesteps.
    if (syncEnabled || selectedTimeInStore === validSelectedTime) {
      return;
    }
    dispatch(
      soundingsActions.updateSelectedTime({
        selectedTime: validSelectedTime,
        viewId,
        origin: SoundingsActionOrigin.soundingsComponent,
      }),
    );
  }, [dispatch, selectedTimeInStore, syncEnabled, validSelectedTime, viewId]);

  if (!isTimeSliderVisible) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          paddingRight: 1,
          paddingBottom: 1,
        }}
      >
        <HideSliderButton
          isVisible={false}
          toggleTimeSliderIsVisible={toggleTimeSliderVisibility}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <SoundingsTimeSlider
        selectedTime={selectedTime}
        timesteps={mergedTimesteps}
        updateSelectedTime={updateSelectedTime}
      />
      <Box sx={{ position: 'absolute', right: 0, bottom: 0, padding: 1 }}>
        <HideSliderButton
          isVisible
          toggleTimeSliderIsVisible={toggleTimeSliderVisibility}
        />
      </Box>
    </Box>
  );
};
